/**
 * Generic Shop Orders Service
 * WooCommerce, Shopware 6, Magento 2, BigCommerce, PrestaShop, İkas — backend
 * adapter registry (/api/shop-orders) üzerinden, Shopify biçiminde sipariş verisi.
 * Shopify ve JTL kendi servislerini kullanmaya devam eder.
 */

import api from '../lib/api';
import { formatDate } from '@/lib/format';
import { t } from '@/i18n/t';
import type { ShopifyOrder, ShopifyApiResponse } from './shopify.service';
import shopIntegrationsService, { PlatformType } from './shop-integrations.service';

export interface ShopOrdersResponse extends ShopifyApiResponse {
  data: ShopifyApiResponse['data'] & {
    meta: ShopifyApiResponse['data']['meta'] & {
      platform: PlatformType;
      platformName: string;
      integrationName?: string;
    };
  };
}

const PAYMENT_LABELS: Record<string, [string, string]> = {
  paid: ['ticket:orderStatus.paid', 'Bezahlt'],
  pending: ['ticket:orderStatus.pending', 'Ausstehend'],
  authorized: ['ticket:orderStatus.authorized', 'Autorisiert'],
  partially_paid: ['ticket:orderStatus.partiallyPaid', 'Teilweise bezahlt'],
  refunded: ['ticket:orderStatus.refunded', 'Rückerstattet'],
  partially_refunded: ['ticket:orderStatus.partiallyRefunded', 'Teilweise rückerstattet'],
  voided: ['ticket:orderStatus.voided', 'Storniert'],
  failed: ['ticket:orderStatus.failed', 'Fehlgeschlagen'],
};

const FULFILLMENT_LABELS: Record<string, [string, string]> = {
  fulfilled: ['ticket:fulfillment.fulfilled', 'Versandt'],
  partial: ['ticket:fulfillment.partial', 'Teilweise versandt'],
  unfulfilled: ['ticket:fulfillment.unfulfilled', 'Nicht versandt'],
  restocked: ['ticket:fulfillment.restocked', 'Wieder eingelagert'],
  returned: ['ticket:fulfillment.returned', 'Retourniert'],
  cancelled: ['ticket:fulfillment.cancelled', 'Storniert'],
  pending: ['ticket:fulfillment.pending', 'Ausstehend'],
};

const addressOf = (a: any) => {
  const x = a || {};
  const parts = [x.address1, x.address2].filter(Boolean).join(', ');
  const cityLine = [x.zip, x.city].filter(Boolean).join(' ');
  return {
    name: [x.first_name, x.last_name].filter(Boolean).join(' '),
    company: x.company || null,
    address1: x.address1 || '',
    address2: x.address2 || null,
    city: x.city || '',
    zip: x.zip || '',
    country: x.country || '',
    phone: x.phone || null,
    fullAddress: [parts, cityLine, x.country].filter(Boolean).join(', '),
  };
};

class ShopOrdersService {
  async getOrdersByEmail(email: string, organizationId?: string, platform?: PlatformType, limit = 10): Promise<ShopOrdersResponse> {
    const response = await api.get<ShopOrdersResponse>(`/shop-orders/${encodeURIComponent(email)}`, {
      params: { organizationId, platform, limit },
    });
    return response.data;
  }

  async getOrderByNumber(orderNumber: string, organizationId?: string, platform?: PlatformType): Promise<ShopOrdersResponse> {
    const response = await api.get<ShopOrdersResponse>(`/shop-orders/number/${encodeURIComponent(orderNumber)}`, {
      params: { organizationId, platform },
    });
    return response.data;
  }

  translatePaymentStatus(status: string | null | undefined): string {
    const entry = PAYMENT_LABELS[String(status || 'pending')];
    return entry ? t(entry[0], entry[1]) : String(status || '');
  }

  translateFulfillmentStatus(status: string | null | undefined): string {
    const entry = FULFILLMENT_LABELS[String(status || 'pending')];
    return entry ? t(entry[0], entry[1]) : String(status || '');
  }

  /**
   * Normalize edilmiş siparişi TicketDetail'in beklediği UI biçimine çevirir
   * (shopify.service.formatOrderForUI ile aynı alanlar, null-güvenli).
   */
  formatOrderForUI(order: ShopifyOrder, platform?: PlatformType) {
    const o = order as any;
    const type: PlatformType | undefined = platform || o.platform;
    return {
      id: o.id,
      platform: type,
      orderNumber: order.orderNumber,
      confirmationNumber: order.confirmationNumber,
      date: formatDate(order.createdAt),
      paymentStatus: this.translatePaymentStatus(order.financialStatus),
      paymentMethod: o.paymentMethod || null,
      // Für Erstattungen über PayPal/Stripe/Klarna (Magento, BigCommerce …)
      transactionId: o.transactionId || null,
      shopStatus: o.statusLabel || null,
      fulfillmentStatus: this.translateFulfillmentStatus(order.fulfillmentStatus),
      trackingNumber: o.trackingNumber || null,
      trackingUrl: o.trackingUrl || null,
      carrier: o.carrier || null,
      total: `${order.totalPrice} ${order.currency}`,
      currency: order.currency,
      totalDiscounts: order.totalDiscounts,
      items: (order.lineItems || []).map((item) => ({
        sku: item.sku,
        name: item.name,
        quantity: item.quantity,
        price: `${item.price} ${order.currency}`,
        vendor: item.vendor,
        variantTitle: item.variantTitle,
        warehouse: (item as any).warehouse,
      })),
      customer: order.customer ? {
        id: order.customer.id != null ? String(order.customer.id) : '',
        name: [order.customer.firstName, order.customer.lastName].filter(Boolean).join(' '),
        email: order.customer.email,
        phone: order.customer.phone,
        verified: order.customer.verified,
      } : null,
      shippingAddress: addressOf(order.shippingAddress),
      billingAddress: addressOf(order.billingAddress),
      trackingNumbers: (order.fulfillments || []).flatMap((f: any) =>
        (f.tracking_numbers || []).map((n: string, i: number) => ({ number: n, url: f.tracking_urls?.[i] || undefined, carrier: f.tracking_company || undefined }))
      ),
      discounts: order.discountCodes || [],
      shopType: type ? shopIntegrationsService.getPlatformDisplayName(type) : 'Shop',
      orderStatusUrl: order.orderStatusUrl,
      adminUrl: order.adminUrl,
    };
  }
}

export const shopOrdersService = new ShopOrdersService();
export default shopOrdersService;
